// Copyright (c) 2026 Nathan Blackman / Find My Trout. All rights reserved.

import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { fetchWVCounties } from '../utils/streamService';
import { getWeatherByCoords, getWeatherIcon } from '../utils/weatherService';
import WeatherModal from './WeatherModal';
import StockingReportsModal from './StockingReportsModal';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const MapView = ({ streams, favorites, selectedStreamId, onStreamDeselect, onToggleFavorite }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const markersByStreamIdRef = useRef({});
  const countyLayerRef = useRef(null);
  const [weatherModalData, setWeatherModalData] = useState(null);
  const [stockingModalData, setStockingModalData] = useState(null);
  const initialCenter = useRef([38.9000, -80.4549]);
  const initialZoom = useRef(8);

  useEffect(() => {
    if (!mapRef.current) return;

    if (!mapInstanceRef.current) {
      const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
      if (isMobile) initialZoom.current = 7;
      mapInstanceRef.current = L.map(mapRef.current).setView(initialCenter.current, initialZoom.current);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 18,
      }).addTo(mapInstanceRef.current);

      loadCountyBoundaries();
    }

    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
    markersByStreamIdRef.current = {};

    if (streams && streams.length > 0) {
      streams.forEach(stream => {
        if (stream.coordinates) {
          const isFavorite = favorites && favorites.includes(stream.id);
          const starSvg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#F59E0B" stroke="#fff" stroke-width="1.5"><polygon points="12,2 15,9 22,9 17,14 18,22 12,18 6,22 7,14 2,9 9,9"/></svg>';
          const icon = isFavorite
            ? L.divIcon({
                html: `<div class="leaflet-star-marker">${starSvg}</div>`,
                className: 'custom-marker custom-marker-star',
                iconSize: [28, 28],
                iconAnchor: [14, 28]
              })
            : L.icon({
                iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
                iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
                shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34]
              });

          const marker = L.marker([stream.coordinates.lat, stream.coordinates.lng], { icon })
            .addTo(mapInstanceRef.current);

          const countyDisplay = [stream.county, stream.county2, stream.county3]
            .filter(Boolean)
            .join(', ');

          // Create popup with loading weather
          const popupContent = document.createElement('div');
          popupContent.className = 'stream-popup-container';
          
          const favLabel = isFavorite ? 'Remove from favorites' : 'Add to favorites';
          const favClass = isFavorite ? 'stream-popup-favorite-btn is-favorite' : 'stream-popup-favorite-btn';
          let detailsHTML = `
            <div class="stream-popup-header">
              <h3 class="stream-popup-title">${stream.name}</h3>
              <button type="button" class="${favClass}" data-stream-id="${stream.id}" aria-label="${favLabel}" title="${favLabel}">★</button>
            </div>`;
          
          if (countyDisplay) {
            detailsHTML += `<p class="stream-popup-detail"><strong>County:</strong> ${countyDisplay}</p>`;
          }
          if (stream.mileage) {
            detailsHTML += `<p class="stream-popup-detail"><strong>Length:</strong> ${stream.mileage} miles</p>`;
          }
          if (stream.regType) {
            detailsHTML += `<p class="stream-popup-detail"><strong>Regulation:</strong> ${stream.regType}</p>`;
          }
          if (stream.stockCode && stream.stockCode !== 'Unknown') {
            detailsHTML += `<p class="stream-popup-detail"><strong>Trout Type:</strong> ${stream.stockCode}</p>`;
          }
          if (stream.nearCity) {
            detailsHTML += `<p class="stream-popup-detail"><strong>Near:</strong> ${stream.nearCity}</p>`;
          }
          if (stream.hatchery) {
            detailsHTML += `<p class="stream-popup-detail"><strong>Hatchery:</strong> ${stream.hatchery}</p>`;
          }
          if (stream.stockExtent) {
            detailsHTML += `<p class="stream-popup-detail"><strong>Stock Extent:</strong> ${stream.stockExtent}</p>`;
          }
          
          detailsHTML += `
            <div class="stream-popup-weather" id="weather-${stream.id}">
              <p class="weather-loading">Loading weather...</p>
            </div>
            <div class="stream-popup-stocking-cta">
              <button type="button" id="view-stocking-${stream.id}" class="stream-popup-stocking-btn">
                View Stocking Reports →
              </button>
            </div>
          `;
          
          popupContent.innerHTML = detailsHTML;

          const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
          const popupClass = isMobile ? 'custom-popup custom-popup-mobile' : 'custom-popup';
          const autoPanTop = isMobile ? 140 : 100;

          marker.bindPopup(popupContent, {
            maxWidth: isMobile ? 320 : 300,
            className: popupClass,
            autoPan: true,
            autoPanPaddingTopLeft: L.point(20, autoPanTop),
            autoPanPaddingBottomRight: L.point(20, 24)
          });

          // Ensure popup is fully visible (same behavior on desktop and mobile)
          const ensurePopupFullyVisible = (map, marker) => {
            const popup = marker.getPopup();
            if (!popup || !popup.isOpen()) return;
            const popupEl = popup.getElement();
            if (!popupEl) return;
            const popupRect = popupEl.getBoundingClientRect();
            const mapContainer = map.getContainer();
            const mapRect = mapContainer.getBoundingClientRect();
            const paddingTop = 20;
            const paddingBottom = 24;
            const paddingSides = 20;
            const minTop = mapRect.top + paddingTop;
            const maxBottom = mapRect.bottom - paddingBottom;
            const minLeft = mapRect.left + paddingSides;
            const maxRight = mapRect.right - paddingSides;
            let panX = 0;
            let panY = 0;
            if (popupRect.top < minTop) panY = minTop - popupRect.top;
            else if (popupRect.bottom > maxBottom) panY = -(popupRect.bottom - maxBottom);
            if (popupRect.left < minLeft) panX = minLeft - popupRect.left;
            else if (popupRect.right > maxRight) panX = -(popupRect.right - maxRight);
            if (panX !== 0 || panY !== 0) {
              map.panBy(L.point(-panX, -panY), { animate: true, duration: 0.25 });
            }
          };

          marker.on('popupopen', async (e) => {
            const map = mapInstanceRef.current;
            const minTopSpace = 220;
            const px = map.project(e.target._popup._latlng);
            if (px.y > minTopSpace) {
              px.y -= 200;
              const latlng = map.unproject(px);
              map.panTo(latlng, { animate: true, duration: 0.5 });
            }
            setTimeout(() => ensurePopupFullyVisible(map, e.target), 50);

            // Favorite button: toggle favorite for this stream
            const favBtn = popupContent.querySelector('.stream-popup-favorite-btn');
            if (favBtn && typeof onToggleFavorite === 'function') {
              favBtn.replaceWith(favBtn.cloneNode(true)); // remove any prior listener
              const newFavBtn = popupContent.querySelector('.stream-popup-favorite-btn');
              newFavBtn.addEventListener('click', () => onToggleFavorite(stream.id));
            }

            // Stocking reports button: open modal
            const stockingBtn = document.getElementById(`view-stocking-${stream.id}`);
            if (stockingBtn) {
              stockingBtn.addEventListener('click', () => {
                setStockingModalData({ streamId: stream.id, streamName: stream.name });
              });
            }

            // Load weather data
            const weatherDiv = document.getElementById(`weather-${stream.id}`);
            if (weatherDiv) {
              try {
                const weather = await getWeatherByCoords(stream.coordinates.lat, stream.coordinates.lng);
                
                if (weather) {
                  weatherDiv.innerHTML = `
                    <div class="popup-weather-widget">
                      <div class="popup-weather-main">
                        <div>
                          <div class="popup-weather-temp">${weather.temp}°F</div>
                          <div class="popup-weather-description">${weather.description}</div>
                        </div>
                        <div class="popup-weather-icon">${getWeatherIcon(
                          weather.condition,
                          weather.icon && weather.icon.includes('n')
                        )}</div>
                      </div>
                      <div class="popup-weather-details">
                        <span>💧 ${weather.humidity}%</span>
                        <span>💨 ${weather.windSpeed} mph</span>
                        <span>Feels ${weather.feelsLike}°F</span>
                      </div>
                      <button id="view-forecast-${stream.id}" class="popup-forecast-btn">
                        View 5-Day Forecast →
                      </button>
                    </div>
                  `;

                  // Add click handler for forecast button
                  const forecastBtn = document.getElementById(`view-forecast-${stream.id}`);
                  if (forecastBtn) {
                    forecastBtn.addEventListener('click', () => {
                      setWeatherModalData({
                        weather: weather,
                        coordinates: stream.coordinates,
                        streamName: stream.name
                      });
                    });
                  }
                }
                setTimeout(() => ensurePopupFullyVisible(map, e.target), 50);
              } catch (error) {
                console.error('Error loading weather:', error);
                weatherDiv.innerHTML = '<p class="weather-error">Weather unavailable</p>';
                setTimeout(() => ensurePopupFullyVisible(map, e.target), 50);
              }
            }
          });

          // Re-center map when popup closes and clear sidebar selection
          marker.on('popupclose', () => {
            if (typeof onStreamDeselect === 'function') onStreamDeselect();
            const map = mapInstanceRef.current;
            map.setView(initialCenter.current, initialZoom.current, {
              animate: true,
              duration: 0.5
            });
          });

          markersByStreamIdRef.current[stream.id] = marker;
          markersRef.current.push(marker);
        }
      });
    }

    return () => {
      markersRef.current.forEach(marker => marker.remove());
      markersByStreamIdRef.current = {};
    };
  }, [streams, favorites]);

  // When a stream is selected from the sidebar list, open its popup and pan to it
  useEffect(() => {
    if (!selectedStreamId || !mapInstanceRef.current) return;
    const marker = markersByStreamIdRef.current[selectedStreamId];
    if (marker) {
      marker.openPopup();
      mapInstanceRef.current.panTo(marker.getLatLng(), { animate: true, duration: 0.5 });
    }
  }, [selectedStreamId]);

  const loadCountyBoundaries = async () => {
    try {
      const countyData = await fetchWVCounties();
      
      if (countyData && mapInstanceRef.current) {
        if (countyLayerRef.current) {
          countyLayerRef.current.remove();
        }

        countyLayerRef.current = L.geoJSON(countyData, {
          style: {
            color: '#1B6B5E',
            weight: 2,
            opacity: 0.6,
            fillColor: '#1B6B5E',
            fillOpacity: 0.05
          },
          onEachFeature: (feature, layer) => {
            if (feature.properties.NAME) {
              layer.bindPopup(`
                <div class="stream-popup-container">
                  <h4 class="stream-popup-title">${feature.properties.NAME} County</h4>
                </div>
              `);
            }
          }
        }).addTo(mapInstanceRef.current);
      }
    } catch (error) {
      console.error('Error loading county boundaries:', error);
    }
  };

  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <div ref={mapRef} id="map" style={{ height: '100%', width: '100%' }} />
      
      <WeatherModal
        isOpen={!!weatherModalData}
        onClose={() => setWeatherModalData(null)}
        weather={weatherModalData?.weather}
        coordinates={weatherModalData?.coordinates}
        streamName={weatherModalData?.streamName}
      />
      <StockingReportsModal
        isOpen={!!stockingModalData}
        onClose={() => setStockingModalData(null)}
        streamId={stockingModalData?.streamId}
        streamName={stockingModalData?.streamName}
      />
    </>
  );
};

export default MapView;
